import angular from 'angular';
import booksService from '../../util/BooksSvc/BooksSvc';
import entitySelectionTemplate from './entitySelection.template.html';

export default angular
  .module('books.ui.entitySelection', [booksService])
  .directive('entitySelection', entitySelectionDirective).name;

/*******************************************************************************
 * Directive: entitySelection
 *
 * Displays a template for displaying the entity types and the individual name
 * of any entity type when the individual option is selected. The name field is
 * disabled if the entity type selection is for all of any entity type.
 *
 * csmValidators is required to be on at least one <div> in the index page. This
 * works with the CSM validators and is used to validate the input of an entity
 * name when required.
 *
 * @attr {object} entity
 * Optional Control object for resetting the entity
 *
 * @attr  {object} model
 * Required. Report request object model
 *
 * @attr  {string} selectorLabel
 * Optional. Text for the label. When not defined on the index page "Entities"
 * will be displayed.
 *
 * @attr  {string} labelFormatClass
 * Optional. Space separated list of CSS classes for the label of the template
 *
 * @attr  {string} inputFormatClass
 * Optional. Space separated list of CSS classes for the input of the template
 *
 * @attr  {string} spanFormatClass
 * Optional. Space separated list of CSS classes for the span of the template
 *
 * */
/* @ngInject */
function entitySelectionDirective(BooksSvc, SERVER, $q) {
  return {
    require: '^^csmValidators',
    template: entitySelectionTemplate,
    scope: {
      entity: '=',
      model: '=',
      selectorLabel: '@',
      labelFormatClass: '@',
      inputFormatClass: '@',
      spanFormatClass: '@'
    },
    link: function ($scope, element, attr) {
      $scope.data = {};

      /*--------------------------------------*/
      /* Array of entity dropdown options and */
      /* values to be sent in the request     */
      /*--------------------------------------*/
      $scope.entityList = [
        { text: 'All Entities', useName: false, entityType: 0 },
        {
          text: 'All Students',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_STUDENT
        },
        {
          text: 'All Teachers',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_TEACHER
        },
        {
          text: 'All Parents',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_PARENT
        },
        {
          text: 'All Grandparents',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_GRANDPARENT
        },
        {
          text: 'All Community Partners',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER
        },
        {
          text: 'All Vendors',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_VENDOR
        },
        {
          text: 'All Families',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_FAMILY
        },
        {
          text: 'All Others',
          useName: false,
          entityType: SERVER.BooksDBConstants.ET_OTHER
        },
        {
          text: 'Student Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_STUDENT
        },
        {
          text: 'Teacher Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_TEACHER
        },
        {
          text: 'Parent Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_PARENT
        },
        {
          text: 'Grandparent Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_GRANDPARENT
        },
        {
          text: 'Community Partner Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER
        },
        {
          text: 'Vendor Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_VENDOR
        },
        {
          text: 'Family Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_FAMILY
        },
        {
          text: 'Other Name',
          useName: true,
          entityType: SERVER.BooksDBConstants.ET_OTHER
        }
      ];
      var mEntityOption = $scope.entityList[0];

      /*-----------------------------------*/
      /* Grandparent and Community Partner */
      /* types are only available to PTA.  */
      /*-----------------------------------*/
      if (!SERVER.currentUser.orgType.startsWith('PTA')) {
        $scope.entityList.removeItem(
          $scope.entityList.find(function (element) {
            return element.entityType == SERVER.BooksDBConstants.ET_GRANDPARENT;
          })
        );
        $scope.entityList.removeItem(
          $scope.entityList.find(function (element) {
            return (
              element.entityType == SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER
            );
          })
        );
      }

      /*----------------------------------------------------------------*/
      /* Watches for changes to the entity selections. The entity type, */
      /* entity name and entity id will be submitted on the request     */
      /*----------------------------------------------------------------*/
      $scope.$watch('data.entity', function (entity) {
        var entitySelected =
          $scope.data.entity && angular.isObject($scope.data.entity);
        $scope.model.entityID = entitySelected ? $scope.data.entity.id : 0;
        $scope.model.entityName = entitySelected ? null : $scope.data.entity;
      });

      /*****************************************************************************
       * entityOption */
      /**
       * Checks to see if the entity type selection has changed. If so, it removes the
       * entity name selected and disables the entity name input if the selection is for
       * "All" of any entity type. Getter monitors the entity type dropdown for changes.
       * Setter sets the value of the entity names based on the selection of the entity type.
       *
       * @param  option  Current entity model value
       * @return         Entity options
       *****************************************************************************/
      $scope.entityOption = function (option) {
        /* Getter Mode. */
        if (arguments.length == 0) return mEntityOption;

        /* Setter Mode. */
        var typeChanged =
          !mEntityOption ||
          !option ||
          mEntityOption.entityType != option.entityType;
        mEntityOption = option;
        $scope.model.entityType = option ? option.entityType : '';

        if (typeChanged || !mEntityOption.useName) $scope.data.entity = null;
      };

      /*-----------------------------------------------------------------*/
      /* Resets the entity type to the first selection in the entityList */
      /*-----------------------------------------------------------------*/
      if ($scope.entity) {
        $scope.entity.reset = function () {
          return $scope.entityOption($scope.entityList[0]);
        };
      }

      /*****************************************************************************
       * getEntityNames */
      /**
       * Dataprovider source for the entity name input. Returns a promise for the
       * type ahead directive to wait for.
       *
       * @param  text  Current type ahead text
       * @return       Promise object for server response
       *****************************************************************************/
      $scope.getEntityNames = function (text) {
        var defer = $q.defer();
        BooksSvc.findEntities(text, mEntityOption.entityType).then(
          function (resp) {
            defer.resolve(resp.data);
          }
        );
        return defer.promise;
      };
    }
  };
}
